import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Checkbox, 
  TextField,
  Chip,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { 
  message, 
  Empty 
} from 'antd';
import { Search, Button } from '@voicecare.ai/ui-library';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTheme } from '../../ThemeContext';
import ListModal from '../common/ListModal';
import LogoLoader from '../common/loaders/gif-logo-loader';
import DepartmentService from '../../services/department-service';

const { Title, Text } = Typography;

/**
 * ProviderSelectionModal Component
 * 
 * Modal for selecting providers to add to a department with role assignment
 * Takes inspiration from AffiliationManager for role management
 */
const ProviderSelectionModal = ({
  open,
  departmentId,
  facilityId,
  departmentName,
  facilityName,
  currentProviderIds = [],
  onClose,
  onSave
}) => {
  const { themeMode, currentTheme } = useTheme();
  
  // State management
  const [allProviders, setAllProviders] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Provider selection and role management
  const [selectedProviderIds, setSelectedProviderIds] = useState([]);
  const [providerRoles, setProviderRoles] = useState({}); // { providerId: ['role1', 'role2'] }
  const [roleInputs, setRoleInputs] = useState({}); // { providerId: 'currentInput' }
  
  // Error handling
  const [validationErrors, setValidationErrors] = useState({});

  /**
   * Load all providers for the facility
   */
  const loadProviders = useCallback(async () => {
    if (!facilityId) return;

    setLoading(true);
    try {
      const result = await DepartmentService.getProvidersByFacility(facilityId);
      
      if (result.success) {
        const providers = result.data || [];
        setAllProviders(providers);
        
        console.log('🔍 Debug - Raw facility providers:', providers.map(p => ({
          id: p._id,
          name: p.fullName,
          affiliations: p.affiliations
        })));
        console.log('🔍 Debug - Current provider IDs to exclude:', currentProviderIds);
        
        // Filter out providers already in the department
        const available = providers.filter(provider => 
          !currentProviderIds.includes(provider._id)
        );
        setAvailableProviders(available);
        
        console.log('✅ Loaded providers for facility:', {
          total: providers.length,
          available: available.length,
          currentInDepartment: currentProviderIds.length
        });
        
        console.log('🔍 Debug - Available providers after filtering:', available.map(p => ({
          id: p._id,
          name: p.fullName
        })));
      } else {
        console.error('❌ Failed to load providers:', result.message);
        message.error(result.message || 'Failed to load providers');
      }
    } catch (error) {
      console.error('❌ Error loading providers:', error);
      message.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [facilityId, currentProviderIds]);

  // Load providers when modal opens
  useEffect(() => {
    if (open) {
      loadProviders();
    }
  }, [open, loadProviders]);

  /**
   * Reset modal state when closed
   */
  useEffect(() => {
    if (!open) {
      setSelectedProviderIds([]);
      setProviderRoles({});
      setRoleInputs({});
      setValidationErrors({});
      setSearchQuery('');
    }
  }, [open]);

  /**
   * Filter providers based on search query
   */
  const filteredProviders = availableProviders.filter(provider => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      provider.fullName?.toLowerCase().includes(query) ||
      provider.npi?.toLowerCase().includes(query) ||
      provider.specialties?.some(specialty => 
        specialty.toLowerCase().includes(query)
      )
    );
  });

  /**
   * Handle provider selection
   */
  const handleProviderSelect = (providerId, checked) => {
    setSelectedProviderIds(prev => {
      if (checked) {
        return [...prev, providerId];
      } else {
        // Remove provider and clear their roles
        const newSelected = prev.filter(id => id !== providerId);
        setProviderRoles(prevRoles => {
          const newRoles = { ...prevRoles };
          delete newRoles[providerId];
          return newRoles;
        });
        setRoleInputs(prevInputs => {
          const newInputs = { ...prevInputs };
          delete newInputs[providerId];
          return newInputs;
        });
        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[providerId];
          return newErrors;
        });
        return newSelected;
      }
    });
  };

  /**
   * Handle role input change for a provider
   */
  const handleRoleInputChange = (providerId, value) => {
    setRoleInputs(prev => ({
      ...prev,
      [providerId]: value
    }));
  };

  /**
   * Add role to a provider
   */
  const addRoleToProvider = (providerId) => {
    const roleValue = roleInputs[providerId]?.trim();
    if (!roleValue) return;

    setProviderRoles(prev => {
      const existingRoles = prev[providerId] || [];
      if (!existingRoles.includes(roleValue)) {
        return {
          ...prev,
          [providerId]: [...existingRoles, roleValue]
        };
      }
      return prev;
    });

    // Clear input
    setRoleInputs(prev => ({
      ...prev,
      [providerId]: ''
    }));

    // Clear validation error if roles exist
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[providerId];
      return newErrors;
    });
  };

  /**
   * Remove role from a provider
   */
  const removeRoleFromProvider = (providerId, roleToRemove) => {
    setProviderRoles(prev => ({
      ...prev,
      [providerId]: (prev[providerId] || []).filter(role => role !== roleToRemove)
    }));
  };

  /**
   * Handle role input key press (Enter to add)
   */
  const handleRoleKeyPress = (e, providerId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRoleToProvider(providerId);
    }
  };

  /**
   * Validate that all selected providers have at least one role
   */
  const validateProviderRoles = () => {
    const errors = {};
    selectedProviderIds.forEach(providerId => {
      const roles = providerRoles[providerId] || [];
      if (roles.length === 0) {
        errors[providerId] = 'At least one role is required';
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle save providers
   */
  const handleSave = async () => {
    if (selectedProviderIds.length === 0) {
      message.error('Please select at least one provider');
      return;
    }

    if (!validateProviderRoles()) {
      message.error('Please assign at least one role to each selected provider');
      return;
    }

    setSaving(true);

    try {
      // Transform selected providers to the API format
      const renderingProviderIds = selectedProviderIds.map(providerId => ({
        id: providerId,
        role: providerRoles[providerId] || ['Provider'] // Default fallback
      }));

      console.log('🏥 Adding providers to department:', {
        departmentId,
        renderingProviderIds
      });

      const result = await DepartmentService.addProvidersToDepartment(
        departmentId, 
        renderingProviderIds
      );

      if (result.success) {
        message.success(`Successfully added ${selectedProviderIds.length} provider(s) to ${departmentName}`);
        onSave();
      } else {
        message.error(result.message || 'Failed to add providers to department');
      }
    } catch (error) {
      console.error('❌ Error adding providers:', error);
      message.error('Failed to add providers to department');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Get provider display name
   */
  const getProviderName = (provider) => {
    return provider.fullName || provider.name || 'Unknown Provider';
  };

  /**
   * Render provider role management section
   */
  const renderProviderRoles = (provider) => {
    const providerId = provider._id;
    const roles = providerRoles[providerId] || [];
    const hasError = validationErrors[providerId];
    
    return (
      <Box sx={{ mt: 1, ml: 3 }}>
        {/* Current roles */}
        {roles.length > 0 && (
          <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {roles.map((role, index) => (
              <Chip
                key={index}
                label={role}
                size="small"
                color="primary"
                onDelete={() => removeRoleFromProvider(providerId, role)}
                deleteIcon={<DeleteOutlined style={{ fontSize: '12px' }} />}
                sx={{ 
                  fontSize: '11px', 
                  height: '24px',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px'
                  }
                }}
              />
            ))}
          </Box>
        )}

        {/* Role input */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="Add role (e.g., Cardiologist, Surgeon)"
            value={roleInputs[providerId] || ''}
            onChange={(e) => handleRoleInputChange(providerId, e.target.value)}
            onKeyPress={(e) => handleRoleKeyPress(e, providerId)}
            error={!!hasError}
            helperText={hasError}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: '12px',
                backgroundColor: themeMode === 'dark' ? '#2d2a45' : '#ffffff',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '10px',
                margin: '2px 0 0 0'
              }
            }}
          />
          <IconButton
            size="small"
            onClick={() => addRoleToProvider(providerId)}
            disabled={!(roleInputs[providerId]?.trim())}
            sx={{
              backgroundColor: '#e63e2b',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d73527'
              },
              '&.Mui-disabled': {
                backgroundColor: '#ccc',
                color: '#666'
              }
            }}
          >
            <PlusOutlined style={{ fontSize: '14px' }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <ListModal
      open={open}
      title={`Add Providers to ${departmentName}`}
      subtitle={`${facilityName} - Select providers and assign roles`}
      data={[]} // We handle rendering manually
      onClose={onClose}
      maxWidth={800}
      showIndex={false}
      customActions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            label="Cancel"
            disabled={saving}
            style={{ borderRadius: "6px" }}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={selectedProviderIds.length === 0 || saving}
            label={saving ? "Adding..." : `Add ${selectedProviderIds.length} Provider${selectedProviderIds.length !== 1 ? 's' : ''}`}
            style={{ borderRadius: "6px" }}
          />
        </Box>
      }
      renderItem={() => null} // We handle rendering manually
    >
      <Box sx={{ p: 2 }}>
        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            themeMode={themeMode}
            currentTheme={currentTheme}
            placeholder="Search providers by name, NPI, or specialty..."
            fontSize="14px"
            style={{ width: '100%' }}
          />
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '200px',
            flexDirection: 'column',
            gap: 2
          }}>
            <div style={{ 
              width: '60px', 
              height: '60px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <LogoLoader />
            </div>
            <Text style={{ 
              fontSize: '14px', 
              color: themeMode === 'dark' ? '#ffffff' : '#666666' 
            }}>
              Loading available providers...
            </Text>
          </Box>
        )}

        {/* No providers available */}
        {!loading && filteredProviders.length === 0 && (
          <Empty
            description={
              availableProviders.length === 0
                ? "No available providers found for this facility"
                : "No providers match your search"
            }
            style={{ margin: '2rem 0' }}
          />
        )}

        {/* Provider list */}
        {!loading && filteredProviders.length > 0 && (
          <>
            {/* Info about role requirements */}
            <Alert 
              severity="info" 
              sx={{ mb: 2, fontSize: '12px' }}
            >
              <strong>Role Assignment Required:</strong> Each selected provider must have at least one role assigned (e.g., Cardiologist, Surgeon, Primary Care).
            </Alert>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {filteredProviders.map((provider, index) => {
                const isSelected = selectedProviderIds.includes(provider._id);
                const hasValidationError = validationErrors[provider._id];

                return (
                  <Box
                    key={provider._id || index}
                    sx={{
                      border: `1px solid ${
                        hasValidationError 
                          ? '#f44336'
                          : isSelected 
                            ? '#e63e2b' 
                            : themeMode === 'dark' ? '#404040' : '#e9e8ea'
                      }`,
                      borderRadius: '8px',
                      p: 2,
                      backgroundColor: themeMode === 'dark' ? '#2d2a45' : '#ffffff',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Provider header with checkbox */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleProviderSelect(provider._id, e.target.checked)}
                        size="small"
                        sx={{
                          color: '#e63e2b',
                          '&.Mui-checked': { color: '#e63e2b' },
                          p: 0,
                          mr: 1,
                          mt: 0.25
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Text strong style={{
                          fontSize: '14px',
                          color: themeMode === 'dark' ? '#ffffff' : '#000000',
                          display: 'block'
                        }}>
                          {getProviderName(provider)}
                        </Text>
                        {provider.npi && (
                          <Text style={{
                            fontSize: '12px',
                            color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
                            display: 'block'
                          }}>
                            NPI: {provider.npi}
                          </Text>
                        )}
                        {provider.specialties && provider.specialties.length > 0 && (
                          <Text style={{
                            fontSize: '12px',
                            color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
                            display: 'block'
                          }}>
                            Specialties: {provider.specialties.slice(0, 3).join(', ')}
                            {provider.specialties.length > 3 && ` +${provider.specialties.length - 3} more`}
                          </Text>
                        )}
                      </Box>
                    </Box>

                    {/* Role management (only show for selected providers) */}
                    {isSelected && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ ml: 3 }}>
                          <Text style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: themeMode === 'dark' ? '#ffffff' : '#333333',
                            marginBottom: '8px',
                            display: 'block'
                          }}>
                            Assign Roles:
                          </Text>
                          {renderProviderRoles(provider)}
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </ListModal>
  );
};

export default ProviderSelectionModal;
